import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Bell, Moon, ShieldCheck, CreditCard, KeyRound, Smartphone, MonitorSmartphone, Loader2, Mail, History, ShieldAlert, Trash2, User as UserIcon, Building2, Phone, Receipt, Download, Database, Ban, CheckCircle2, CalendarClock, ArrowUpRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { logError } from "@/lib/logger";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/pages/Dashboard";
import { DataExportPanel } from "@/pages/DataExport";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

function Row({ icon: Icon, title, desc, children }) {
  return (
    <div className="flex items-center justify-between gap-4 py-4 border-b border-white/8 last:border-0">
      <div className="flex items-start gap-3 min-w-0">
        <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 text-[#D4AF37]" />
        </div>
        <div className="min-w-0">
          <div className="text-white font-medium">{title}</div>
          <div className="text-sm text-[#71717A] break-words">{desc}</div>
        </div>
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

const OUTLINE_BTN = "border-white/15 bg-transparent text-white hover:bg-white/5 rounded-full";

function ChangePasswordDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent(""); setNext(""); onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121214] border-white/10 text-white" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle>Change password</DialogTitle>
          <DialogDescription className="text-[#71717A]">Enter your current password and choose a new one.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-[#D4D4D8] mb-1.5 block">Current password</Label>
            <Input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)}
              data-testid="current-password-input" className="bg-[#0A0A0B] border-white/10 text-white h-11" />
          </div>
          <div>
            <Label className="text-[#D4D4D8] mb-1.5 block">New password</Label>
            <Input type="password" required minLength={6} value={next} onChange={(e) => setNext(e.target.value)}
              data-testid="new-password-input" placeholder="At least 6 characters" className="bg-[#0A0A0B] border-white/10 text-white h-11" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={loading} data-testid="change-password-submit-btn"
              className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-lg hover:opacity-90">
              {loading ? "Updating…" : "Update password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TwoFADialog({ open, onOpenChange, onEnabled }) {
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) { setSetup(null); setCode(""); return; }
    (async () => {
      try {
        const { data } = await api.post("/auth/2fa/setup");
        setSetup(data);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || err.message);
        onOpenChange(false);
      }
    })();
  }, [open, onOpenChange]);

  const enable = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/2fa/enable", { code });
      toast.success("Two-factor authentication enabled");
      onEnabled();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121214] border-white/10 text-white" data-testid="twofa-setup-dialog">
        <DialogHeader>
          <DialogTitle>Enable two-factor authentication</DialogTitle>
          <DialogDescription className="text-[#71717A]">Scan the QR code with Google Authenticator, Authy, or Microsoft Authenticator.</DialogDescription>
        </DialogHeader>
        {!setup ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" /></div>
        ) : (
          <form onSubmit={enable} className="space-y-4">
            <div className="flex justify-center">
              <img src={setup.qr_data_url} alt="2FA QR code" width={192} height={192}
                className="rounded-lg bg-white p-2" data-testid="twofa-qr" />
            </div>
            <div className="text-center">
              <p className="text-xs text-[#71717A] mb-1">Can't scan? Enter this key manually:</p>
              <code className="text-xs text-[#D4AF37] break-all" data-testid="twofa-secret">{setup.secret}</code>
            </div>
            <div>
              <Label className="text-[#D4D4D8] mb-1.5 block">Enter the 6-digit code to confirm</Label>
              <Input inputMode="numeric" required value={code} onChange={(e) => setCode(e.target.value)} maxLength={6}
                data-testid="twofa-enable-code-input" placeholder="123456"
                className="bg-[#0A0A0B] border-white/10 text-white h-11 tracking-[0.4em] text-center" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={loading} data-testid="twofa-enable-submit-btn"
                className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-lg hover:opacity-90">
                {loading ? "Verifying…" : "Enable 2FA"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DisableTwoFADialog({ open, onOpenChange, onDisabled }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const disable = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/2fa/disable", { code });
      toast.success("Two-factor authentication disabled");
      setCode(""); onDisabled(); onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121214] border-white/10 text-white" data-testid="twofa-disable-dialog">
        <DialogHeader>
          <DialogTitle>Disable two-factor authentication</DialogTitle>
          <DialogDescription className="text-[#71717A]">Enter a current code from your authenticator app to confirm.</DialogDescription>
        </DialogHeader>
        <form onSubmit={disable} className="space-y-4">
          <Input inputMode="numeric" required value={code} onChange={(e) => setCode(e.target.value)} maxLength={6}
            data-testid="twofa-disable-code-input" placeholder="123456"
            className="bg-[#0A0A0B] border-white/10 text-white h-11 tracking-[0.4em] text-center" />
          <DialogFooter>
            <Button type="submit" disabled={loading} data-testid="twofa-disable-submit-btn"
              className="bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 rounded-lg">
              {loading ? "Disabling…" : "Disable 2FA"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function describeSession(ua) {
  if (!ua) return "Unknown device";
  if (/mobile|android|iphone/i.test(ua)) return "Mobile device";
  if (/curl|python|http/i.test(ua)) return "API client";
  const browsers = [[/edg/i, "Edge"], [/chrome/i, "Chrome"], [/firefox/i, "Firefox"], [/safari/i, "Safari"]];
  const match = browsers.find(([re]) => re.test(ua));
  return `${match ? match[1] : "Browser"} on desktop`;
}

function SessionsSection() {
  const [sessions, setSessions] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/sessions");
      setSessions(data);
    } catch (err) { logError("Settings", "Load sessions failed", err?.message); setSessions([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const revoke = async (id) => {
    try { await api.delete(`/auth/sessions/${id}`); toast.success("Session revoked"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const revokeOthers = async () => {
    try { const { data } = await api.post("/auth/sessions/revoke-others"); toast.success(`Signed out ${data.revoked} other session(s)`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  return (
    <div className="rounded-2xl border border-white/8 bg-[#121214] p-7 mb-6" data-testid="sessions-section">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-heading text-xl text-white">Active sessions</h3>
        {sessions && sessions.length > 1 && (
          <Button onClick={revokeOthers} variant="outline" data-testid="revoke-others-btn"
            className={OUTLINE_BTN + " text-sm h-8"}>Sign out others</Button>
        )}
      </div>
      <p className="text-sm text-[#71717A] mb-4">Devices currently signed in to your account.</p>
      {sessions === null ? (
        <div className="flex items-center gap-2 text-[#71717A] py-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : sessions.map((s) => (
        <div key={s.session_id} className="flex items-center justify-between gap-4 py-3 border-b border-white/8 last:border-0" data-testid="session-row">
          <div className="flex items-center gap-3">
            <MonitorSmartphone className="w-5 h-5 text-[#A1A1AA]" />
            <div>
              <div className="text-white text-sm">{describeSession(s.user_agent)}
                {s.current && <span className="ml-2 text-[10px] uppercase tracking-wider text-[#D4AF37]">This device</span>}</div>
              <div className="text-xs text-[#71717A]">{s.ip || "—"} · last active {new Date(s.last_seen).toLocaleString()}</div>
            </div>
          </div>
          {!s.current && (
            <Button onClick={() => revoke(s.session_id)} variant="outline" data-testid="revoke-session-btn"
              className={OUTLINE_BTN + " text-sm h-8"}>Revoke</Button>
          )}
        </div>
      ))}
    </div>
  );
}

function ChangeEmailDialog({ open, onOpenChange, onChanged }) {
  const [newEmail, setNewEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/change-email", { new_email: newEmail, password });
      toast.success(data?.message || "Email updated");
      setNewEmail(""); setPassword("");
      onChanged?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121214] border-white/10 text-white" data-testid="change-email-dialog">
        <DialogHeader>
          <DialogTitle>Change email</DialogTitle>
          <DialogDescription className="text-[#71717A]">Enter your new email address and confirm with your password.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-[#D4D4D8] mb-1.5 block">New email</Label>
            <Input type="email" required value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
              data-testid="new-email-input" placeholder="you@newdomain.com" className="bg-[#0A0A0B] border-white/10 text-white h-11" />
          </div>
          <div>
            <Label className="text-[#D4D4D8] mb-1.5 block">Current password</Label>
            <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="change-email-password-input" className="bg-[#0A0A0B] border-white/10 text-white h-11" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={loading} data-testid="change-email-submit-btn"
              className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-lg hover:opacity-90">
              {loading ? "Updating…" : "Update email"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const SECURITY_EVENT_LABELS = {
  login: "Signed in",
  logout: "Signed out",
  logout_all: "Signed out of all devices",
  password_changed: "Password changed",
  password_reset: "Password reset",
  email_changed: "Email address changed",
  "2fa_enabled": "Two-factor authentication enabled",
  "2fa_disabled": "Two-factor authentication disabled",
  session_revoked: "A device session was revoked",
  sessions_revoked_others: "Signed out other devices",
};

function SecurityActivitySection() {
  const [events, setEvents] = useState(null);

  useEffect(() => {
    let active = true;
    api.get("/auth/security-activity")
      .then(({ data }) => { if (active) setEvents(data); })
      .catch((err) => { logError("Settings", "Load security activity failed", err?.message); if (active) setEvents([]); });
    return () => { active = false; };
  }, []);

  return (
    <div className="rounded-2xl border border-white/8 bg-[#121214] p-7 mb-6" data-testid="security-activity-section">
      <h3 className="font-heading text-xl text-white mb-2">Security activity</h3>
      <p className="text-sm text-[#71717A] mb-4">Recent security events on your account.</p>
      {events === null ? (
        <div className="flex items-center gap-2 text-[#71717A] py-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : events.length === 0 ? (
        <p className="text-sm text-[#71717A] py-2" data-testid="security-activity-empty">No activity recorded yet.</p>
      ) : events.map((ev) => (
        <div key={ev.event_id} className="flex items-center justify-between gap-4 py-3 border-b border-white/8 last:border-0" data-testid="security-activity-row">
          <div className="flex items-center gap-3">
            <History className="w-5 h-5 text-[#A1A1AA] shrink-0" />
            <div>
              <div className="text-white text-sm">{SECURITY_EVENT_LABELS[ev.event] || ev.event}</div>
              <div className="text-xs text-[#71717A]">{ev.ip || "—"} · {new Date(ev.created_at).toLocaleString()}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function DeleteAccountDialog({ open, onOpenChange, onDeleted }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/delete-account", { password, confirm, origin_url: window.location.origin });
      toast.success(data?.message || "Account scheduled for deletion");
      onDeleted?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121214] border-red-500/20 text-white" data-testid="delete-account-dialog">
        <DialogHeader>
          <DialogTitle className="text-red-400">Delete account</DialogTitle>
          <DialogDescription className="text-[#71717A]">
            This cancels your subscription and schedules permanent deletion after a 7-day recovery period.
            You can restore your account by signing in during that window.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-[#D4D4D8] mb-1.5 block">Confirm your password</Label>
            <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="delete-account-password-input" className="bg-[#0A0A0B] border-white/10 text-white h-11" />
          </div>
          <div>
            <Label className="text-[#D4D4D8] mb-1.5 block">Type <span className="text-red-400 font-mono">DELETE</span> to confirm</Label>
            <Input required value={confirm} onChange={(e) => setConfirm(e.target.value)}
              data-testid="delete-account-confirm-input" placeholder="DELETE" className="bg-[#0A0A0B] border-white/10 text-white h-11" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={loading || confirm !== "DELETE"} data-testid="delete-account-submit-btn"
              className="bg-red-500/90 text-white font-semibold rounded-lg hover:bg-red-500 disabled:opacity-50">
              {loading ? "Deleting…" : "Delete my account"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ProfilePanel({ onDirtyChange }) {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [company, setCompany] = useState(user?.company || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [saving, setSaving] = useState(false);

  const dirty = name !== (user?.name || "") || company !== (user?.company || "") || phone !== (user?.phone || "");

  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/profile", { name, company, phone });
      await refresh();
      onDirtyChange?.(false);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="settings-profile-panel">
      <div className="rounded-2xl border border-white/8 bg-[#121214] p-7 mb-6 flex items-center gap-5">
        {user?.picture ? (
          <img src={user.picture} alt="" className="w-20 h-20 rounded-full object-cover" />
        ) : (
          <div className="w-20 h-20 rounded-full fv-gold-gradient flex items-center justify-center text-[#0A0A0B] font-heading text-3xl font-bold">
            {(user?.name || user?.email || "U")[0].toUpperCase()}
          </div>
        )}
        <div>
          <div className="font-heading text-2xl text-white">{user?.name || "—"}</div>
          <div className="text-[#71717A]">{user?.email}</div>
          <div className="mt-1 text-xs text-[#D4AF37] uppercase tracking-wider capitalize">{user?.role} · {user?.subscription?.tier || "no"} plan</div>
        </div>
      </div>
      <form onSubmit={save} className="rounded-2xl border border-white/8 bg-[#121214] p-7 space-y-5" data-testid="profile-form">
        <div>
          <Label className="text-[#D4D4D8] mb-1.5 block">Full name</Label>
          <div className="relative">
            <UserIcon className="w-4 h-4 text-[#71717A] absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input"
              className="bg-[#0d0d0f] border-white/10 text-white h-11 pl-9" />
          </div>
        </div>
        <div>
          <Label className="text-[#D4D4D8] mb-1.5 block">Company</Label>
          <div className="relative">
            <Building2 className="w-4 h-4 text-[#71717A] absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={company} onChange={(e) => setCompany(e.target.value)} data-testid="profile-company-input"
              placeholder="Your business name" className="bg-[#0d0d0f] border-white/10 text-white h-11 pl-9" />
          </div>
        </div>
        <div>
          <Label className="text-[#D4D4D8] mb-1.5 block">Phone</Label>
          <div className="relative">
            <Phone className="w-4 h-4 text-[#71717A] absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="profile-phone-input"
              placeholder="+1 (555) 000-0000" className="bg-[#0d0d0f] border-white/10 text-white h-11 pl-9" />
          </div>
        </div>
        <div>
          <Label className="text-[#D4D4D8] mb-1.5 block">Email</Label>
          <Input value={user?.email || ""} disabled className="bg-[#0d0d0f] border-white/10 text-[#71717A] h-11" />
        </div>
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={saving || !dirty} data-testid="profile-save-btn"
            className="fv-gold-gradient text-[#0A0A0B] font-semibold h-11 rounded-lg hover:opacity-90 px-8 disabled:opacity-50">
            {saving ? "Saving…" : "Save changes"}
          </Button>
          {dirty && <span className="text-xs text-amber-400" data-testid="profile-unsaved-indicator">Unsaved changes</span>}
        </div>
      </form>
    </div>
  );
}

function SecurityPanel() {
  const { user, refresh } = useAuth();
  const [pwOpen, setPwOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [twoFAOpen, setTwoFAOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const isPasswordUser = user?.auth_provider === "password";
  const twoFAEnabled = user?.two_fa_enabled;

  return (
    <div className="rounded-2xl border border-white/8 bg-[#121214] p-7" data-testid="security-section">
      <h3 className="font-heading text-xl text-white mb-1">Security</h3>
      <p className="text-sm text-[#71717A] mb-4">Protect your account with a strong password and two-factor authentication.</p>
      {isPasswordUser && (
        <Row icon={Mail} title="Email address" desc={user?.email || "Change your account email"}>
          <Button onClick={() => setEmailOpen(true)} variant="outline" data-testid="open-change-email-btn" className={OUTLINE_BTN}>Change</Button>
        </Row>
      )}
      {isPasswordUser && (
        <Row icon={KeyRound} title="Password" desc="Change your account password">
          <Button onClick={() => setPwOpen(true)} variant="outline" data-testid="open-change-password-btn" className={OUTLINE_BTN}>Change</Button>
        </Row>
      )}
      <Row icon={Smartphone} title="Two-factor authentication"
        desc={twoFAEnabled ? "Enabled — an extra layer of security is active" : "Add an extra layer of security with an authenticator app"}>
        {twoFAEnabled ? (
          <div className="flex items-center gap-3">
            <span className="text-xs text-emerald-400 uppercase tracking-wider" data-testid="twofa-status-enabled">Enabled</span>
            <Button onClick={() => setDisableOpen(true)} variant="outline" data-testid="open-disable-2fa-btn"
              className="border-red-500/30 bg-transparent text-red-400 hover:bg-red-500/10 rounded-full">Disable</Button>
          </div>
        ) : (
          <Button onClick={() => setTwoFAOpen(true)} variant="outline" data-testid="open-enable-2fa-btn" className={OUTLINE_BTN}>Enable</Button>
        )}
      </Row>
      <Row icon={ShieldCheck} title="Email verification"
        desc={user?.is_verified ? "Your email address is verified" : "Your email is not yet verified"}>
        <span className={`text-xs uppercase tracking-wider ${user?.is_verified ? "text-emerald-400" : "text-amber-400"}`} data-testid="verify-status">
          {user?.is_verified ? "Verified" : "Unverified"}
        </span>
      </Row>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      <ChangeEmailDialog open={emailOpen} onOpenChange={setEmailOpen} onChanged={refresh} />
      <TwoFADialog open={twoFAOpen} onOpenChange={setTwoFAOpen} onEnabled={refresh} />
      <DisableTwoFADialog open={disableOpen} onOpenChange={setDisableOpen} onDisabled={refresh} />
    </div>
  );
}

function useSubscriptionInfo() {
  const [info, setInfo] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/billing/subscription"); setInfo(data); return data; }
    catch (err) { logError("Settings", "Load subscription failed", err?.message); setInfo({}); return {}; }
  }, []);
  useEffect(() => { load(); }, [load]);
  return [info, load];
}

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "—");

function BillingPanel() {
  const navigate = useNavigate();
  const [info] = useSubscriptionInfo();
  const sub = info?.subscription;

  return (
    <div className="rounded-2xl border border-white/8 bg-[#121214] p-7" data-testid="settings-billing-panel">
      <h3 className="font-heading text-xl text-white mb-1">Billing</h3>
      <p className="text-sm text-[#71717A] mb-5">Your plan, payment method, and invoices.</p>
      {info === null ? (
        <div className="flex items-center gap-2 text-[#71717A] py-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <>
          <Row icon={Receipt} title="Current plan"
            desc={sub?.tier ? `${sub.tier} · ${sub.status}` : "No active subscription"}>
            <span className="text-[#D4AF37] font-heading text-lg capitalize">{sub?.tier || "Free"}</span>
          </Row>
          <Row icon={CalendarClock} title={sub?.cancel_at_period_end ? "Access until" : "Next billing date"}
            desc={sub?.cancel_at_period_end ? "Your plan is scheduled to cancel" : "When your plan renews next"}>
            <span className="text-white text-sm">{fmtDate(sub?.cancel_at_period_end ? sub?.cancels_at : sub?.next_billing_date)}</span>
          </Row>
          <Row icon={CreditCard} title="Payment method" desc="Card used for billing">
            <span className="text-white text-sm">{info?.payment_method?.description || "None on file"}</span>
          </Row>
          <div className="mt-5">
            <Button onClick={() => navigate("/billing")} data-testid="settings-open-billing-btn"
              className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-lg hover:opacity-90">
              Manage billing, plans & invoices <ArrowUpRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function SubscriptionPanel() {
  const navigate = useNavigate();
  const [info, reload] = useSubscriptionInfo();
  const [busy, setBusy] = useState(false);
  const sub = info?.subscription;
  const status = sub?.status;

  const reactivate = async () => {
    setBusy(true);
    try { await api.post("/billing/reactivate"); toast.success("Subscription reactivated"); await reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not reactivate"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-white/8 bg-[#121214] p-7" data-testid="settings-subscription-panel">
      <h3 className="font-heading text-xl text-white mb-1">Subscription</h3>
      <p className="text-sm text-[#71717A] mb-5">Your current subscription status and options.</p>
      {info === null ? (
        <div className="flex items-center gap-2 text-[#71717A] py-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-xl fv-gold-gradient flex items-center justify-center shrink-0"><Receipt className="w-5 h-5 text-[#0A0A0B]" /></div>
            <div>
              <div className="text-white font-medium capitalize" data-testid="settings-subscription-status">
                {sub?.tier || "Free"} {status ? `· ${sub?.cancel_at_period_end ? "cancels at period end" : status}` : "· no active subscription"}
              </div>
              <div className="text-sm text-[#71717A]">
                {sub?.cancel_at_period_end
                  ? `Access until ${fmtDate(sub?.cancels_at)} (${sub?.access_ends_in_days}d left)`
                  : status === "trialing" ? `Trial · ${sub?.days_remaining}d remaining`
                  : status === "active" ? `Renews ${fmtDate(sub?.next_billing_date)}`
                  : "Reactivate or pick a plan to unlock full access."}
              </div>
            </div>
          </div>
          {(sub?.cancel_at_period_end || status === "canceled" || status === "expired") ? (
            <Button onClick={reactivate} disabled={busy} data-testid="settings-reactivate-btn"
              className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-lg hover:opacity-90">
              <CheckCircle2 className="w-4 h-4 mr-2" /> {busy ? "Reactivating…" : (sub?.cancel_at_period_end ? "Resume subscription" : "Reactivate subscription")}
            </Button>
          ) : (
            <Button onClick={() => navigate("/billing")} variant="outline" data-testid="settings-change-plan-btn" className={OUTLINE_BTN}>
              Change plan <ArrowUpRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </>
      )}
    </div>
  );
}

function CancelSubscriptionPanel() {
  const [info, reload] = useSubscriptionInfo();
  const [busy, setBusy] = useState(false);
  const sub = info?.subscription;
  const status = sub?.status;
  const canCancel = ["active", "trialing", "past_due"].includes(status) && !sub?.cancel_at_period_end;
  const alreadyScheduled = sub?.cancel_at_period_end;
  const alreadyCanceled = ["canceled", "expired"].includes(status);

  const cancel = async () => {
    setBusy(true);
    try { await api.post("/billing/cancel"); toast.success("Your subscription has been canceled."); await reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not cancel"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-amber-500/20 bg-[#121214] p-7" data-testid="settings-cancel-panel">
      <h3 className="font-heading text-xl text-white mb-1">Cancel subscription</h3>
      <p className="text-sm text-[#71717A] mb-5">Stop future renewals. Your data is always preserved.</p>
      {info === null ? (
        <div className="flex items-center gap-2 text-[#71717A] py-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : alreadyCanceled ? (
        <div className="rounded-xl border border-amber-500/25 bg-amber-500/5 p-4 text-sm text-amber-300" data-testid="cancel-already-canceled">
          Your subscription is already canceled and your account is read-only. You can still sign in, download your data & invoices, and contact support. Reactivate anytime from the Subscription tab.
        </div>
      ) : alreadyScheduled ? (
        <div className="rounded-xl border border-amber-500/25 bg-amber-500/5 p-4 text-sm text-amber-300" data-testid="cancel-already-scheduled">
          Your subscription is scheduled to cancel on <strong>{fmtDate(sub?.cancels_at)}</strong>. You keep full access until then. You can resume anytime from the Subscription tab.
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-amber-500/25 bg-amber-500/5 p-4 text-sm text-amber-300 mb-5" data-testid="cancel-warning">
            <div className="flex items-start gap-2">
              <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-amber-200 mb-1">What happens when you cancel</p>
                <ul className="list-disc list-inside space-y-1 text-amber-300/90">
                  <li>{status === "past_due" ? "Billing stops immediately and your account becomes read-only." : "Your plan won't renew — you keep full access until the end of your current period, then the account becomes read-only."}</li>
                  <li>All your data, documents, progress, and completed courses are <strong>never deleted</strong>.</li>
                  <li>You can reactivate anytime to restore full access.</li>
                  <li>You can still sign in, download your data & invoices, and contact support in read-only mode.</li>
                </ul>
              </div>
            </div>
          </div>
          {canCancel ? (
            <Button onClick={cancel} disabled={busy} data-testid="settings-cancel-subscription-btn"
              className="bg-amber-500/90 text-[#0A0A0B] font-semibold rounded-lg hover:bg-amber-500">
              <Ban className="w-4 h-4 mr-2" /> {busy ? "Canceling…" : "Cancel my subscription"}
            </Button>
          ) : (
            <p className="text-sm text-[#71717A]" data-testid="cancel-no-subscription">You don't have an active subscription to cancel.</p>
          )}
        </>
      )}
    </div>
  );
}

function DataPrivacyPanel() {
  const { user, refresh } = useAuth();
  const prefs = user?.notification_prefs || { email_alerts: true, product_updates: false, security_alerts: true };
  const [savingPref, setSavingPref] = useState(false);

  const savePref = async (key, value) => {
    setSavingPref(true);
    try {
      await api.put("/profile/notifications", { ...prefs, [key]: value });
      await refresh();
      toast.success("Preferences saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSavingPref(false); }
  };

  return (
    <div data-testid="settings-privacy-panel">
      <div className="rounded-2xl border border-white/8 bg-[#121214] p-7 mb-6">
        <h3 className="font-heading text-xl text-white mb-1">Notification preferences</h3>
        <p className="text-sm text-[#71717A] mb-4">Choose what we email you about.</p>
        <Row icon={Bell} title="Email notifications" desc="Receive important account alerts by email">
          <Switch checked={prefs.email_alerts} disabled={savingPref}
            onCheckedChange={(v) => savePref("email_alerts", v)} data-testid="toggle-email-notif" />
        </Row>
        <Row icon={Moon} title="Product updates" desc="Get notified about new modules and features">
          <Switch checked={prefs.product_updates} disabled={savingPref}
            onCheckedChange={(v) => savePref("product_updates", v)} data-testid="toggle-product-updates" />
        </Row>
        <Row icon={ShieldAlert} title="Security alerts" desc="Be notified about logins and security changes">
          <Switch checked={prefs.security_alerts} disabled={savingPref}
            onCheckedChange={(v) => savePref("security_alerts", v)} data-testid="toggle-security-alerts" />
        </Row>
      </div>
      <div className="rounded-2xl border border-white/8 bg-[#121214] p-7" data-testid="privacy-statement">
        <h3 className="font-heading text-xl text-white mb-1">Your data & privacy</h3>
        <p className="text-sm text-[#71717A] mb-4">You're always in control of your information.</p>
        <ul className="space-y-3 text-sm text-[#A1A1AA]">
          <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" /> Your data is encrypted in transit and never sold to third parties.</li>
          <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" /> You can export a full copy of your data anytime from the Download Data tab.</li>
          <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" /> Canceling or downgrading never deletes your data — it stays safe in read-only mode.</li>
          <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" /> Deleting your account has a 7-day recovery window before anything is permanently removed.</li>
        </ul>
      </div>
    </div>
  );
}

function DownloadDataPanel() {
  return (
    <div data-testid="settings-download-panel">
      <div className="rounded-2xl border border-white/8 bg-[#121214] p-7 mb-6">
        <h3 className="font-heading text-xl text-white mb-1">Download your data</h3>
        <p className="text-sm text-[#71717A]">Export any part of your account. Downloads are delivered as secure links that expire in 24 hours.</p>
      </div>
      <DataExportPanel />
    </div>
  );
}

function DeleteAccountPanel() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const isPasswordUser = user?.auth_provider === "password";

  return (
    <div className="rounded-2xl border border-red-500/25 bg-[#121214] p-7" data-testid="settings-delete-panel">
      <h3 className="font-heading text-xl text-red-400 mb-1">Delete account</h3>
      <p className="text-sm text-[#71717A] mb-5">Permanently delete your Fairview Systems account and all associated data.</p>
      <div className="rounded-xl border border-red-500/25 bg-red-500/5 p-4 text-sm text-red-300 mb-5">
        <div className="flex items-start gap-2">
          <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium text-red-200 mb-1">This is a serious, irreversible action</p>
            <ul className="list-disc list-inside space-y-1 text-red-300/90">
              <li>Your subscription is canceled immediately.</li>
              <li>Your account is scheduled for <strong>permanent deletion after a 7-day recovery window</strong>.</li>
              <li>You can restore your account by signing in during those 7 days.</li>
              <li>After the recovery window ends, all data is permanently erased and cannot be recovered.</li>
            </ul>
          </div>
        </div>
      </div>
      {isPasswordUser ? (
        <Button onClick={() => setDeleteOpen(true)} data-testid="open-delete-account-btn"
          className="bg-red-500/90 text-white font-semibold rounded-lg hover:bg-red-500">
          <Trash2 className="w-4 h-4 mr-2" /> Delete account
        </Button>
      ) : (
        <p className="text-sm text-[#71717A]">Your account uses Google sign-in. Contact support to delete your account.</p>
      )}
      <DeleteAccountDialog open={deleteOpen} onOpenChange={setDeleteOpen}
        onDeleted={() => { setUser(false); navigate("/login"); }} />
    </div>
  );
}

function LogoutPanel() {
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (err) { logError("Settings", "Logout request failed", err); }
    setUser(false);
    toast.success("Signed out");
    navigate("/login");
  };

  const logoutAll = async () => {
    try {
      const { data } = await api.post("/auth/logout-all");
      toast.success(`Signed out of ${(data?.revoked ?? 0) + 1} device(s)`);
    } catch (err) { logError("Settings", "Logout-all request failed", err); }
    setUser(false);
    navigate("/login");
  };

  return (
    <div className="rounded-2xl border border-white/8 bg-[#121214] p-7" data-testid="settings-logout-panel">
      <h3 className="font-heading text-xl text-white mb-1">Log out</h3>
      <p className="text-sm text-[#71717A] mb-5">Sign out of Fairview Systems on this device, or everywhere at once.</p>
      <div className="flex flex-col sm:flex-row gap-3">
        <Button onClick={logout} data-testid="settings-logout-btn"
          className="bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 rounded-lg">
          <LogOut className="w-4 h-4 mr-2" /> Sign out
        </Button>
        <Button onClick={logoutAll} data-testid="settings-logout-all-btn"
          className="bg-transparent border border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-lg">
          <MonitorSmartphone className="w-4 h-4 mr-2" /> Log out of all devices
        </Button>
      </div>
    </div>
  );
}

function EmailPreferencesPanel() {
  const [cats, setCats] = useState([]);
  const [prefs, setPrefs] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/account/email-preferences").then(({ data }) => {
      setCats(data.categories); setPrefs(data.preferences);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const toggle = async (key, val) => {
    const next = { ...prefs, [key]: val };
    setPrefs(next);
    try { await api.put("/account/email-preferences", { preferences: next }); toast.success("Preferences saved"); }
    catch { toast.error("Could not save"); }
  };

  return (
    <div className="rounded-2xl border border-white/8 bg-[#121214] p-7" data-testid="email-preferences-section">
      <h3 className="font-heading text-xl text-white mb-1">Email Preferences</h3>
      <p className="text-sm text-[#71717A] mb-5">Choose which emails you'd like to receive. Security and billing emails are always sent.</p>
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#D4AF37]" /></div>
      ) : cats.map((c) => (
        <Row key={c.key} icon={Mail} title={c.label} desc={`Receive ${c.label.toLowerCase()} emails`}>
          <Switch checked={prefs[c.key] !== false} onCheckedChange={(v) => toggle(c.key, v)} data-testid={`email-pref-${c.key}`} />
        </Row>
      ))}
      <div className="mt-4 flex items-center gap-2 text-xs text-[#52525B]">
        <ShieldCheck className="w-3.5 h-3.5 text-[#D4AF37]" /> Security &amp; billing emails are always delivered
      </div>
    </div>
  );
}

const SETTINGS_NAV = [
  { key: "profile", label: "Profile", icon: UserIcon },
  { key: "security", label: "Security", icon: ShieldCheck },
  { key: "sessions", label: "Active Sessions", icon: MonitorSmartphone },
  { key: "notifications", label: "Email Preferences", icon: Bell },
  { key: "billing", label: "Billing", icon: CreditCard },
  { key: "subscription", label: "Subscription", icon: Receipt },
  { key: "privacy", label: "Data & Privacy", icon: Database },
  { key: "download", label: "Download Data", icon: Download },
  { key: "cancel", label: "Cancel Subscription", icon: Ban, danger: true },
  { key: "delete", label: "Delete Account", icon: Trash2, danger: true },
  { key: "logout", label: "Log Out", icon: LogOut, danger: true },
];

export default function Settings() {
  const [active, setActive] = useState("profile");
  const [pendingTab, setPendingTab] = useState(null);
  const dirtyRef = useRef(false);

  const requestTab = (key) => {
    if (key === active) return;
    if (active === "profile" && dirtyRef.current) { setPendingTab(key); return; }
    setActive(key);
  };

  const discardAndSwitch = () => {
    dirtyRef.current = false;
    setActive(pendingTab);
    setPendingTab(null);
  };

  return (
    <div className="fv-fade-up max-w-5xl">
      <PageHeader eyebrow="Account" title="Account Settings" subtitle="Manage your profile, security, billing, and privacy — all in one place." />

      <div className="flex flex-col lg:flex-row gap-6">
        <nav className="lg:w-64 shrink-0" data-testid="settings-nav">
          <div className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible rounded-2xl border border-white/8 bg-[#121214] p-2">
            {SETTINGS_NAV.map(({ key, label, icon: Icon, danger }) => (
              <button key={key} onClick={() => requestTab(key)} data-testid={`settings-nav-${key}`}
                className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-sm whitespace-nowrap transition-colors text-left w-full ${
                  active === key
                    ? "bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/25"
                    : `border border-transparent hover:bg-white/5 ${danger ? "text-red-400/90" : "text-[#A1A1AA]"}`}`}>
                <Icon className="w-4 h-4 shrink-0" /> {label}
              </button>
            ))}
          </div>
        </nav>

        <div className="flex-1 min-w-0">
          {active === "profile" && <ProfilePanel onDirtyChange={(d) => { dirtyRef.current = d; }} />}
          {active === "security" && <SecurityPanel />}
          {active === "sessions" && (<><SessionsSection /><SecurityActivitySection /></>)}
          {active === "notifications" && <EmailPreferencesPanel />}
          {active === "billing" && <BillingPanel />}
          {active === "subscription" && <SubscriptionPanel />}
          {active === "privacy" && <DataPrivacyPanel />}
          {active === "download" && <DownloadDataPanel />}
          {active === "cancel" && <CancelSubscriptionPanel />}
          {active === "delete" && <DeleteAccountPanel />}
          {active === "logout" && <LogoutPanel />}
        </div>
      </div>

      <Dialog open={pendingTab !== null} onOpenChange={(o) => { if (!o) setPendingTab(null); }}>
        <DialogContent className="bg-[#121214] border-amber-500/25 text-white" data-testid="unsaved-changes-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-400">Discard unsaved changes?</DialogTitle>
            <DialogDescription className="text-[#71717A]">
              You have unsaved changes to your profile. If you leave now, they'll be lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPendingTab(null)} data-testid="unsaved-keep-editing-btn"
              className={OUTLINE_BTN}>Keep editing</Button>
            <Button onClick={discardAndSwitch} data-testid="unsaved-discard-btn"
              className="bg-amber-500/90 text-[#0A0A0B] font-semibold rounded-lg hover:bg-amber-500">
              Discard changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
